import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  IconWorld,
  IconRefresh,
  IconPalette,
  IconTrophy,
  IconChevronRight,
} from '@tabler/icons-react';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import MythicLogo from '../assets/logos/80ies_TdF_Logo.png';
import nakasendoCut2Preview from '../assets/images/Nakasendo_preview_cut2.png';
import Footer from '../components/Footer';
import { HeroVisual } from './ZwiftFixPage';
import { startStravaConnect } from '../utils/stravaConnect';
import { CHALLENGE_CATALOG, getFinisherMedalUrl } from '../data/challengeCatalog';
import useJsonLd from '../seo/useJsonLd';
import './ChallengePage.css';
import './ZwiftFixPage.css';
import './HomeLandingPage.css';

// The signed-out umbrella homepage: one brand orientation line, then the three
// products as stacked demo sections (Art Maps first, then the Zwift fix), every
// section in the same zfix hero layout (framed visual + caption on the left,
// headline/body/Strava CTA/freenote on the right) so the page reads as one UI.
// Each CTA returns the rider to its own product after OAuth, and a learn-more
// link leads to the full landing page. Signed-in "/" redirects to /art in
// App.js, so this page only ever renders logged-out.

// Art before/after: the same portrait onboarding assets the /maps aha mock uses
// (shared with the art-feed onboarding modal), cross-faded like that mock.
const ONBOARDING_ASSET = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fonboarding_modal%2F${file}?alt=media`;
const ART_BEFORE_URL = ONBOARDING_ASSET('OriginalMap3D.png');
const ART_AFTER_URLS = [
  ONBOARDING_ASSET('Japanese3D.png'),
  ONBOARDING_ASSET('StardustBrutalism3D.png'),
  ONBOARDING_ASSET('PocketAdventure3D.png'),
  ONBOARDING_ASSET('MeadowWalk3D.png'),
];
const ART_ROTATE_MS = 2500;

// Same Nakasendo 3D showcase clip (and matching poster) as the /challenge page.
const CHALLENGE_VIDEO_URL =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2FNakasendo_cut2_landscape.mp4?alt=media';

// Mythic World 3D highlight film (3:4 bottom-anchored recut, Itoshima, Japan).
export const MYTHIC_WORLD_VIDEO_URL =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fitoshima-film-recut.mp4?alt=media';
export const MYTHIC_WORLD_POSTER_URL =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fitoshima-film-recut-poster.jpg?alt=media';

const HomeLandingPage = ({ user }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const landingViewedRef = useRef(false);
  const worldSectionRef = useRef(null);
  const fixSectionRef = useRef(null);
  const artSectionRef = useRef(null);
  const challengeSectionRef = useRef(null);
  const [artIndex, setArtIndex] = useState(0);

  // JSON-LD + title (shared source; only injects if the static HTML lacks it).
  useJsonLd('/');

  useEffect(() => {
    if (landingViewedRef.current) return;
    landingViewedRef.current = true;
    posthog?.capture('home_landing_viewed', { is_authenticated: !!user });
  }, [posthog, user]);

  // Rotate the art "after" shot, mirroring the /maps aha mock's cadence.
  useEffect(() => {
    const id = setInterval(() => {
      setArtIndex((i) => (i + 1) % ART_AFTER_URLS.length);
    }, ART_ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  // Each CTA sends the rider back to the product it advertised after the OAuth
  // round-trip (the /event and /invite mechanism): startStravaConnect bypasses
  // the join modal, and StravaAuthCallbackPage honours the explicit return path
  // even for brand-new accounts.
  const handleConnect = (product, location, returnPath) => {
    posthog?.capture('home_landing_strava_clicked', { product, location });
    startStravaConnect(returnPath);
  };

  const handleWorldEarlyAccessConnect = (location = 'section') => {
    try {
      localStorage.setItem('mythicWorldEarlyAccessPending', '1');
    } catch (_) {
      /* storage unavailable */
    }
    posthog?.capture('home_landing_strava_clicked', { product: 'world', location });
    posthog?.capture('mythic_world_early_access_clicked', { source: 'home_landing', location });
    startStravaConnect('/art');
  };

  const handleLearnMore = (product) => {
    posthog?.capture('home_landing_learn_more_clicked', { product });
  };

  // The hero's mini product nav jumps to the matching demo section on this page.
  const handleProductNav = (product, ref) => {
    posthog?.capture('home_landing_product_nav_clicked', { product });
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const featuredChallenge = CHALLENGE_CATALOG.find((c) => c.featured) || CHALLENGE_CATALOG[0];
  const medalUrl = getFinisherMedalUrl(featuredChallenge);

  return (
    <div className="challenge-page home-landing-page">
      <div className="challenge-container">

        {/* Brand hero: orientation only. The demo sections below carry the pitch. */}
        <div className="challenge-header-section">
          <div className="challenge-header">
            <h1>
              <img src={MythicLogo} alt="" className="home-hero-logo" />
              {t('homeLanding.title', 'Mythic Maps')}
            </h1>
            <p className="challenge-subtitle">
              {t('homeLanding.subtitle', 'Turn your activities into art, fix your Zwift map on Strava, and travel legendary routes. Free.')}
            </p>
            <button
              type="button"
              className="challenge-header-strava-cta"
              onClick={() => handleConnect('art', 'hero', '/maps')}
            >
              <img src={stravaConnectButton} alt={t('homeLanding.ctaStrava', 'Connect with Strava, it is free')} />
            </button>
            <div className="home-hero-nav">
              <button type="button" className="home-hero-nav-pill" onClick={() => handleProductNav('world', worldSectionRef)}>
                <IconWorld size={16} />
                {t('homeLanding.navWorld', 'Mythic World')}
                <span className="home-hero-nav-pill-tag">{t('homeLanding.navWorldTag', 'Early Access')}</span>
              </button>
              <button type="button" className="home-hero-nav-pill" onClick={() => handleProductNav('art', artSectionRef)}>
                <IconPalette size={16} />
                {t('homeLanding.navArt', 'Art Maps')}
              </button>
              <button type="button" className="home-hero-nav-pill" onClick={() => handleProductNav('fix', fixSectionRef)}>
                <IconRefresh size={16} />
                {t('homeLanding.navFix', 'Fix my Zwift map')}
              </button>
              <button type="button" className="home-hero-nav-pill" onClick={() => handleProductNav('challenges', challengeSectionRef)}>
                <IconTrophy size={16} />
                {t('homeLanding.navChallenges', 'Challenges')}
              </button>
            </div>
          </div>
        </div>

        {/* Top showcase: Mythic World (3D video highlights & route 3D worlds, early access). */}
        <div className="section-dark" ref={worldSectionRef}>
          <div className="section-inner">
            <span className="home-eyebrow">
              <IconWorld size={14} />
              {t('homeLanding.worldEyebrow', 'Mythic World')}
              <span className="home-eyebrow-badge">{t('homeLanding.worldBadge', 'Early Access')}</span>
            </span>
            <div className="zfix-hero">
              <div className="zfix-hero-visual">
                <figure className="zfix-hero-shot home-challenge-shot home-world-shot">
                  <div className="home-challenge-frame home-world-frame">
                    <video
                      src={MYTHIC_WORLD_VIDEO_URL}
                      poster={MYTHIC_WORLD_POSTER_URL}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                  </div>
                  <figcaption className="zfix-hero-shot-cap zfix-hero-shot-cap-after">
                    {t('homeLanding.worldShotCap', 'Mythic World · 3D highlight film')}
                  </figcaption>
                </figure>
              </div>
              <div className="zfix-hero-text">
                <h2>{t('homeLanding.worldHeadline', '3D video highlights of your activities, and a 3D world built from any route to train in.')}</h2>
                <p>
                  {t(
                    'homeLanding.worldBody',
                    'Every route becomes a living 3D world of real terrain, streets, buildings, and trees. Get cinematic 3D highlight videos of your activities first, and train or relive any route in the world in an immersive 3D world.'
                  )}
                </p>
                <button
                  type="button"
                  className="zfix-strava-cta"
                  onClick={() => handleWorldEarlyAccessConnect('section')}
                >
                  <img src={stravaConnectButton} alt={t('homeLanding.worldCta', 'Connect with Strava to register for Mythic World early access')} />
                </button>
                <p className="zfix-hero-freenote">
                  {t('homeLanding.worldFreeNote', 'Connect with Strava to register for early access · 3D highlight videos will roll out first.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Product demo 1: Art Maps, in the before/after hero layout. */}
        <div className="section-darker" ref={artSectionRef}>
          <div className="section-inner">
            <span className="home-eyebrow">
              <IconPalette size={14} />
              {t('homeLanding.artEyebrow', 'Art Maps')}
            </span>
            <div className="zfix-hero">
              <div className="zfix-hero-visual">
                <figure className="zfix-hero-shot">
                  <img
                    src={ART_BEFORE_URL}
                    alt={t('homeLanding.artBeforeAlt', 'Your activity as a plain map')}
                  />
                  <figcaption className="zfix-hero-shot-cap">{t('homeLanding.artBeforeCap', 'Your activity')}</figcaption>
                </figure>
                <div className="zfix-mock-arrow" aria-hidden="true">→</div>
                <figure className="zfix-hero-shot">
                  <div className="home-shot-stack">
                    {ART_AFTER_URLS.map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt={t('homeLanding.artAfterAlt', 'The same activity as a Mythic Maps art map')}
                        className={i === artIndex ? 'visible' : ''}
                      />
                    ))}
                  </div>
                  <figcaption className="zfix-hero-shot-cap zfix-hero-shot-cap-after">{t('homeLanding.artAfterCap', 'With Mythic Maps')}</figcaption>
                </figure>
              </div>
              <div className="zfix-hero-text">
                <h2>{t('homeLanding.artHeadline', 'Turn your next activity into art, instantly.')}</h2>
                <p>{t('homeLanding.artBody', 'Connect Strava once and we turn your latest activity into an art map on the spot. Over 40 hand-crafted styles, in 2D and 3D.')}</p>
                <button
                  type="button"
                  className="zfix-strava-cta"
                  onClick={() => handleConnect('art', 'section', '/maps')}
                >
                  <img src={stravaConnectButton} alt={t('homeLanding.artCta', 'Connect with Strava and turn my last activity into art')} />
                </button>
                <p className="zfix-hero-freenote">
                  {t('homeLanding.artFreeNote', 'Free to start. No uploads, no GPX files.')}
                </p>
              </div>
            </div>
            <Link to="/maps" className="home-learn-more" onClick={() => handleLearnMore('art')}>
              {t('homeLanding.artLearnMore', 'Explore all 40+ art styles')}
              <IconChevronRight size={16} />
            </Link>
          </div>
        </div>

        {/* Product demo 2: the Zwift fix, the growth wedge, in the same layout. */}
        <div className="section-dark" ref={fixSectionRef}>
          <div className="section-inner">
            <span className="home-eyebrow">
              <IconRefresh size={14} />
              {t('homeLanding.fixEyebrow', 'Fix my Zwift map')}
            </span>
            <div className="zfix-hero">
              <HeroVisual t={t} />
              <div className="zfix-hero-text">
                <h2>{t('homeLanding.fixHeadline', 'Your Zwift rides, finally on the right map.')}</h2>
                <p>{t('homeLanding.fixBody', 'Zwift drops indoor rides in the ocean on Strava. Connect once and we swap in the real world map, automatically.')}</p>
                <button
                  type="button"
                  className="zfix-strava-cta"
                  onClick={() => handleConnect('fix', 'section', '/fix')}
                >
                  <img src={stravaConnectButton} alt={t('homeLanding.fixCta', 'Connect with Strava and fix my last ride')} />
                </button>
                <p className="zfix-hero-freenote">
                  {t('homeLanding.fixFreeNote', 'Free. We never touch your stats. Disconnect in two clicks.')}
                </p>
              </div>
            </div>
            <Link to="/fix" className="home-learn-more" onClick={() => handleLearnMore('fix')}>
              {t('homeLanding.fixLearnMore', 'Learn more about the Zwift fix')}
              <IconChevronRight size={16} />
            </Link>
          </div>
        </div>

        {/* Product demo 3: Challenges, same hero layout with the featured
            journey's 3D map clip as the framed visual. */}
        <div className="section-darker" ref={challengeSectionRef}>
          <div className="section-inner">
            <span className="home-eyebrow">
              <IconTrophy size={14} />
              {t('homeLanding.challengeEyebrow', 'Challenges')}
            </span>
            <div className="zfix-hero">
              <div className="zfix-hero-visual">
                <figure className="zfix-hero-shot home-challenge-shot">
                  <div className="home-challenge-frame">
                    <video
                      src={CHALLENGE_VIDEO_URL}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      poster={nakasendoCut2Preview}
                    />
                    {medalUrl && (
                      <img
                        src={medalUrl}
                        alt={t('homeLanding.challengeMedalAlt', 'Finisher medal')}
                        className="home-challenge-medal"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <figcaption className="zfix-hero-shot-cap zfix-hero-shot-cap-after">{featuredChallenge.title}</figcaption>
                </figure>
              </div>
              <div className="zfix-hero-text">
                <h2>{t('homeLanding.challengeHeadline', 'Every workout carries you along a legendary route.')}</h2>
                <p>{t('homeLanding.challengeBody', 'Join a real-world journey like the Nakasendo. Every run, walk, or ride moves you forward. Hit checkpoints, unlock stories, earn diamonds and a finisher medal.')}</p>
                <button
                  type="button"
                  className="zfix-strava-cta"
                  onClick={() => handleConnect('challenges', 'section', '/challenge')}
                >
                  <img src={stravaConnectButton} alt={t('homeLanding.challengeCta', 'Connect with Strava and start a journey')} />
                </button>
                <p className="zfix-hero-freenote">
                  {t('homeLanding.challengeFreeNote', 'Free. No time limits. Every fitness level counts.')}
                </p>
              </div>
            </div>
            <Link to="/challenge" className="home-learn-more" onClick={() => handleLearnMore('challenges')}>
              {t('homeLanding.challengeLearnMore', 'Browse all challenges')}
              <IconChevronRight size={16} />
            </Link>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default HomeLandingPage;
